<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="520px"
    content-class="add-wrestler-dialog"
    transition="dialog-transition"
  >
    <v-card class="add-wrestler-card">
      <div class="add-wrestler-header">
        <h2 class="add-wrestler-title">
          {{
            edit && wrestler
              ? "Edit " + wrestler.FirstName + " " + wrestler.LastName
              : "Add wrestler"
          }}
        </h2>
        <v-btn icon dark class="add-wrestler-close" @click="resetForm()">
          <v-icon>close</v-icon>
        </v-btn>
      </div>

      <v-card-text class="add-wrestler-body">
        <!-- Duplicate wrestler warning dialog -->
        <v-dialog v-model="showDuplicateDialog" max-width="500px" persistent>
          <v-card>
            <v-card-title class="headline"
              >Potential Duplicate Wrestler(s) Found</v-card-title
            >
            <v-card-text>
              <p>
                A wrestler with a similar name already exists in this school and
                weight class:
              </p>
              <v-list dense>
                <v-list-item v-for="dup in potentialDuplicates" :key="dup.id">
                  <v-list-item-content>
                    <v-list-item-title>{{ dup.FullName }}</v-list-item-title>
                    <v-list-item-subtitle>
                      {{ dup.School }} • {{ dup.RankWeightClass }}
                      {{ dup.Grade ? "• " + dup.Grade : "" }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
              <p class="mt-3">Do you want to create a new wrestler anyway?</p>
            </v-card-text>
            <v-card-actions>
              <v-btn flat color="primary" @click="showDuplicateDialog = false">
                Cancel
              </v-btn>
              <v-spacer></v-spacer>
              <v-btn flat color="primary" @click="cancelAddWrestler()">
                No, go back
              </v-btn>
              <v-btn color="primary" @click="proceedWithAddWrestler()">
                Yes, add anyway
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <div class="add-wrestler-form">
          <!-- Row 1: First name | Last name -->
          <div class="form-row form-row-split">
            <div class="form-row-cell">
              <v-text-field
                v-model="FirstName"
                label="First name"
                outline
                required
                class="form-field"
              />
            </div>
            <div class="form-row-cell">
              <v-text-field
                v-model="LastName"
                label="Last name"
                outline
                required
                class="form-field"
              />
            </div>
          </div>

          <!-- Row 2: School/Team | Grade -->
          <div class="form-row form-row-split">
            <div class="form-row-cell">
              <v-combobox
                v-model="School"
                label="School / Team"
                :items="schools"
                outline
                required
                class="form-field"
              />
            </div>
            <div class="form-row-cell">
              <v-select
                v-model="Grade"
                label="Grade"
                :items="grades"
                outline
                required
                class="form-field"
              />
            </div>
          </div>

          <!-- Row 3: League | Weight class -->
          <div class="form-row form-row-split">
            <div class="form-row-cell">
              <v-select
                v-model="RankLeague"
                :items="leaguesFirestore"
                item-text="text"
                item-value="value"
                label="League"
                outline
                return-object
                class="form-field"
                @input="onLeagueChange"
              />
            </div>
            <div class="form-row-cell">
              <v-select
                v-model="RankWeightClass"
                :items="dynamicWeights"
                label="Weight class"
                outline
                class="form-field"
              />
            </div>
          </div>
        </div>
      </v-card-text>

      <v-card-actions class="add-wrestler-actions">
        <v-btn flat class="action-btn action-cancel" @click="resetForm()">
          Cancel
        </v-btn>
        <v-spacer />
        <v-btn
          color="primary"
          class="action-btn action-save"
          @click="edit ? editWrestler() : insertWrestler()"
        >
          Save
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import {
  getFirestore,
  collection,
  getDocs,
  orderBy,
  query,
  addDoc,
  doc,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { logAuditEntry } from "../utils/auditLog";
export default {
  props: {
    dialog: { type: Boolean, default: false },
    edit: { type: Boolean, default: false },
    wrestler: { type: Object, default: null },
    wrestlerId: { type: String, default: "" },
  },
  data: () => ({
    name: "kennected-add-wrestler",
    FirstName: "",
    LastName: "",
    School: "",
    Grade: "",
    RankLeague: "",
    RankWeightClass: "",
    grades: [
      "K",
      "1st",
      "2nd",
      "3rd",
      "4th",
      "5th",
      "6th",
      "7th",
      "8th",
      "9th",
      "10th",
      "11th",
      "12th",
    ],
    weightclasses: [],
    schools: [],
    showDuplicateDialog: false,
    potentialDuplicates: [],
    pendingWrestlerAdd: false,
  }),
  watch: {
    RankLeague: function (newVal) {
      // console.log("newVal.text => ", newVal.text, newVal.value);
      if (newVal && newVal.value) {
        this.loadWeights(newVal.value);
      }
    },
    wrestler: function () {
      if (this.wrestler != null) {
        this.populateFormFromWrestler();
      }
    },
    leaguesFirestore: function () {
      if (this.wrestler && this.wrestler.RankLeague)
        this.applyLeagueFromWrestler();
    },
    dialog: function (newVal) {
      if (newVal === true) {
        this.getSchoolsFirestore();
        if (this.edit && this.wrestler) {
          this.populateFormFromWrestler();
        } else if (!this.edit) {
          this.resetFormFields();
        }
      }
    },
  },
  methods: {
    populateFormFromWrestler: function () {
      if (!this.wrestler) return;
      this.FirstName = this.wrestler.FirstName || "";
      this.LastName = this.wrestler.LastName || "";
      this.RankWeightClass = this.wrestler.RankWeightClass || "";
      this.School = this.wrestler.School || "";
      this.Grade = this.wrestler.Grade || "";
      this.applyLeagueFromWrestler();
    },
    resetFormFields: function () {
      this.FirstName = "";
      this.LastName = "";
      this.RankWeightClass = "";
      this.RankLeague = "";
      this.School = "";
      this.Grade = "";
    },
    getSchoolsFirestore: function () {
      const db = getFirestore();
      const q = query(collection(db, "schools"), orderBy("Name"));
      getDocs(q).then((querySnapshot) => {
        this.schools = [];
        querySnapshot.forEach((doc) => {
          // doc.data() is never undefined for query doc snapshots
          this.schools.push(doc.data().Name);
          // console.log("weight classes => ", this.weightclasses);
        });
      });
    },
    resetForm: function () {
      (this.FirstName = ""),
        (this.LastName = ""),
        (this.RankWeightClass = ""),
        (this.RankLeague = ""),
        (this.School = ""),
        (this.Grade = ""),
        (this.Image = ""),
        (this.Skill = 0),
        (this.Wins = 0),
        (this.Losses = 0),
        (this.FullName = "");
      // this.dialog = false;
      this.$emit("close-addWrestler");
    },
    getWeightclasses: function (docId) {
      const db = getFirestore();
      const q = query(
        collection(db, "leagues", docId, "weightclasses"),
        orderBy("Weight")
      );
      getDocs(q).then((querySnapshot) => {
        this.weightclasses = [];
        querySnapshot.forEach((doc) => {
          // doc.data() is never undefined for query doc snapshots
          this.weightclasses.push(doc.data().Weight);
          // console.log("weight classes => ", this.weightclasses);
        });
      });
    },
    loadWeights: function (newVal) {
      this.getWeightclasses(newVal);
    },
    onLeagueChange: function (val) {
      if (val && val.value) this.loadWeights(val.value);
    },
    applyLeagueFromWrestler: function () {
      const leagueVal = this.wrestler && this.wrestler.RankLeague;
      if (!leagueVal) {
        this.RankLeague = "";
        return;
      }
      if (this.leaguesFirestore && Array.isArray(this.leaguesFirestore)) {
        const obj = this.leaguesFirestore.find((l) => l.text === leagueVal);
        this.RankLeague = obj || leagueVal;
      } else {
        this.RankLeague = leagueVal;
      }
    },
    insertWrestler: async function () {
      this.pendingWrestlerAdd = true;
      const duplicates = await this.searchForDuplicateWrestlers();

      if (duplicates && duplicates.length > 0) {
        this.potentialDuplicates = duplicates;
        this.showDuplicateDialog = true;
        return;
      }

      this.proceedWithAddWrestler();
    },

    proceedWithAddWrestler: async function () {
      const db = getFirestore();
      try {
        const docRef = await addDoc(collection(db, "athletes"), {
          FirstName: this.FirstName,
          LastName: this.LastName,
          FullName: this.FirstName + " " + this.LastName,
          RankWeightClass: this.RankWeightClass,
          RankLeague:
            (this.RankLeague && this.RankLeague.text) || this.RankLeague || "",
          School: this.School,
          Grade: this.Grade,
          Image: "",
          Skill: 1200,
          Wins: 0,
          Losses: 0,
        });

        // Log wrestler creation - AWAIT this!
        const auth = getAuth();
        const user = auth.currentUser;
        const changes = {
          FirstName: { old: null, new: this.FirstName },
          LastName: { old: null, new: this.LastName },
          FullName: { old: null, new: this.FirstName + " " + this.LastName },
          School: { old: null, new: this.School },
          Grade: { old: null, new: this.Grade },
          RankWeightClass: { old: null, new: this.RankWeightClass },
          RankLeague: {
            old: null,
            new:
              (this.RankLeague && this.RankLeague.text) ||
              this.RankLeague ||
              "",
          },
          Skill: { old: null, new: 0 },
          Wins: { old: null, new: 0 },
          Losses: { old: null, new: 0 },
        };
        await logAuditEntry(
          "wrestler",
          docRef.id,
          "create",
          changes,
          user ? user.uid : "system",
          user ? user.displayName || user.email : "System"
        );

        this.insertSchool(this.School);
        this.showDuplicateDialog = false;
        this.pendingWrestlerAdd = false;
        this.resetForm();
      } catch (error) {}
    },

    cancelAddWrestler: function () {
      this.showDuplicateDialog = false;
      this.pendingWrestlerAdd = false;
    },

    searchForDuplicateWrestlers: async function () {
      try {
        const db = getFirestore();
        const athletesRef = collection(db, "athletes");
        const snapshot = await getDocs(athletesRef);
        const duplicates = [];

        snapshot.forEach((doc) => {
          const athlete = doc.data();
          athlete.id = doc.id;

          // Check if school and weight class match
          if (
            athlete.School === this.School &&
            athlete.RankWeightClass === this.RankWeightClass
          ) {
            // Check for name similarity using a simple algorithm
            if (
              this.isNameSimilar(this.FirstName, athlete.FirstName) &&
              this.isNameSimilar(this.LastName, athlete.LastName)
            ) {
              duplicates.push(athlete);
            }
          }
        });

        return duplicates;
      } catch (error) {
        return [];
      }
    },

    isNameSimilar: function (name1, name2) {
      if (!name1 || !name2) return false;

      const n1 = name1.toLowerCase().trim();
      const n2 = name2.toLowerCase().trim();

      // Exact match
      if (n1 === n2) return true;

      // One contains the other (e.g., "Chris" vs "Christopher")
      if (n1.includes(n2) || n2.includes(n1)) return true;

      // Levenshtein distance: allow up to 2 character differences for names < 5 chars, 3 for longer
      const distance = this.levenshteinDistance(n1, n2);
      const maxDistance = n1.length < 5 ? 2 : 3;
      if (distance <= maxDistance) return true;

      return false;
    },

    levenshteinDistance: function (str1, str2) {
      const len1 = str1.length;
      const len2 = str2.length;
      const matrix = [];

      for (let i = 0; i <= len2; i++) {
        matrix[i] = [i];
      }

      for (let j = 0; j <= len1; j++) {
        matrix[0][j] = j;
      }

      for (let i = 1; i <= len2; i++) {
        for (let j = 1; j <= len1; j++) {
          if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
            matrix[i][j] = matrix[i - 1][j - 1];
          } else {
            matrix[i][j] = Math.min(
              matrix[i - 1][j - 1] + 1,
              matrix[i][j - 1] + 1,
              matrix[i - 1][j] + 1
            );
          }
        }
      }

      return matrix[len2][len1];
    },

    insertSchool: function (schoolName) {
      const db = getFirestore();
      const newSchoolRef = doc(collection(db, "schools"));
      setDoc(newSchoolRef, {
        Name: schoolName,
      })
        .then(function (d) {})
        .catch(function (error) {});
    },

    editWrestler: async function () {
      if (!this.wrestlerId) return;
      const db = getFirestore();
      const wrestlerRef = doc(db, "athletes", this.wrestlerId);
      const rankLeagueText =
        (this.RankLeague && this.RankLeague.text) || this.RankLeague || "";

      // Compute changes for audit log - only for actual changes
      const changes = {};

      // String comparisons with proper null/undefined handling
      const oldFirstName = this.wrestler.FirstName || "";
      const newFirstName = this.FirstName || "";
      if (oldFirstName !== newFirstName) {
        changes.FirstName = {
          old: oldFirstName || null,
          new: newFirstName || null,
        };
      }

      const oldLastName = this.wrestler.LastName || "";
      const newLastName = this.LastName || "";
      if (oldLastName !== newLastName) {
        changes.LastName = {
          old: oldLastName || null,
          new: newLastName || null,
        };
      }

      const oldFullName = this.wrestler.FullName || "";
      const newFullName = (this.FirstName + " " + this.LastName).trim();
      if (oldFullName !== newFullName) {
        changes.FullName = {
          old: oldFullName || null,
          new: newFullName || null,
        };
      }

      const oldSchool = this.wrestler.School || "";
      const newSchool = this.School || "";
      if (oldSchool !== newSchool) {
        changes.School = { old: oldSchool || null, new: newSchool || null };
      }

      const oldGrade = this.wrestler.Grade || "";
      const newGrade = this.Grade || "";
      if (oldGrade !== newGrade) {
        changes.Grade = { old: oldGrade || null, new: newGrade || null };
      }

      const oldRankLeague = this.wrestler.RankLeague || "";
      const newRankLeague = rankLeagueText || "";
      if (oldRankLeague !== newRankLeague) {
        changes.RankLeague = {
          old: oldRankLeague || null,
          new: newRankLeague || null,
        };
      }

      const oldWeightClass = this.wrestler.RankWeightClass || "";
      const newWeightClass = this.RankWeightClass || "";
      if (oldWeightClass !== newWeightClass) {
        changes.RankWeightClass = {
          old: oldWeightClass || null,
          new: newWeightClass || null,
        };
      }

      try {
        await updateDoc(wrestlerRef, {
          FirstName: this.FirstName,
          LastName: this.LastName,
          FullName: this.FirstName + " " + this.LastName,
          School: this.School,
          Grade: this.Grade,
          RankLeague: rankLeagueText,
          RankWeightClass: this.RankWeightClass,
          Skill:
            this.wrestler && this.wrestler.Skill != null
              ? this.wrestler.Skill
              : 200,
          Wins:
            this.wrestler && this.wrestler.Wins != null
              ? this.wrestler.Wins
              : 0,
          Losses:
            this.wrestler && this.wrestler.Losses != null
              ? this.wrestler.Losses
              : 0,
        });

        // Log wrestler update ONLY if there were actual changes - AWAIT this!
        if (Object.keys(changes).length > 0) {
          const auth = getAuth();
          const user = auth.currentUser;

          await logAuditEntry(
            "wrestler",
            this.wrestlerId,
            "update",
            changes,
            user ? user.uid : "system",
            user ? user.displayName || user.email : "System"
          );
        }
        this.$root.$emit("toast", "Wrestler updated successfully", "success");
        this.$emit("close-addWrestler");
      } catch (error) {
        const msg =
          error && error.message ? error.message : "Error updating wrestler.";
        this.$root.$emit("toast", msg, "error");
      }
    },
  },
  asyncComputed: {
    // schoolsFirestore: function() {
    // 	return new Promise((resolve, reject) => {
    //     // firebase.firestore().collection("schools").orderBy("Name").get().then((querySnapshot) => {
    //     firebase.firestore().collection("schools").orderBy("Name").onSnapshot((querySnapshot) => {
    // 			let rows = [];
    //       		querySnapshot.forEach(function(doc) {
    //       			// doc.data() is never undefined for query doc snapshots
    //       			// console.log(doc.id, " => ", doc.data());
    //             // console.log("leagueName => ", doc.data().Name);
    //       			rows.push(doc.data().Name);
    // 			});
    //       // console.log("my rows => ", rows);
    // 			resolve(rows);
    // 		});
    // 	});
    // },
    leaguesFirestore: function () {
      return new Promise((resolve, reject) => {
        const db = getFirestore();
        const q = query(collection(db, "leagues"), orderBy("Name"));
        getDocs(q).then((querySnapshot) => {
          let rows = [];
          querySnapshot.forEach(function (doc) {
            // doc.data() is never undefined for query doc snapshots
            // console.log(doc.id, " => ", doc.data());
            // console.log("leagueName => ", doc.data().Name);
            rows.push({ text: doc.data().Name, value: doc.id });
          });
          // console.log("my rows => ", rows);
          resolve(rows);
        });
      });
    },
    dynamicWeights: function () {
      return this.weightclasses;
    },
  },
};
</script>

<style scoped>
.add-wrestler-card {
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
}

.add-wrestler-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.5rem;
  background: linear-gradient(135deg, #c62828 0%, #b71c1c 100%);
  color: #fff;
}

.add-wrestler-title {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  letter-spacing: -0.02em;
}

.add-wrestler-close {
  margin: 0;
  opacity: 0.8;
  transition: opacity 0.2s;
}

.add-wrestler-close:hover {
  opacity: 1;
}

.add-wrestler-body {
  padding: 1.5rem;
}

.add-wrestler-form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.form-row {
  width: 100%;
}

.form-row-split {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.25rem;
}

.form-row-cell {
  min-width: 0;
}

.form-field {
  width: 100%;
}

.add-wrestler-actions {
  padding: 1rem 1.5rem;
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  border-top: 1px solid #e5e7eb;
  background: #f9fafb;
}

.action-btn {
  min-height: 40px;
  min-width: 100px;
  font-size: 0.95rem;
  border-radius: 4px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.action-cancel {
  color: #374151;
  border: 1px solid #d1d5db;
  background: white;
}

.action-cancel:hover {
  background: #f9fafb;
  border-color: #9ca3af;
}

.action-save {
  background: #2563eb;
  color: white;
}

.action-save:hover {
  background: #1d4ed8;
}

@media (max-width: 768px) {
  .add-wrestler-header {
    padding: 0.75rem 1rem;
  }

  .add-wrestler-title {
    font-size: 1rem;
  }

  .add-wrestler-body {
    padding: 1rem;
  }

  .form-row-split {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .add-wrestler-form {
    gap: 1rem;
  }

  .add-wrestler-actions {
    padding: 0.75rem 1rem;
    flex-direction: column-reverse;
  }

  .action-btn {
    width: 100%;
    min-width: auto;
  }
}
</style>

<style>
/* Global overrides for form inputs inside dialog */
.add-wrestler-dialog .v-input {
  margin-bottom: 0;
}

.add-wrestler-dialog .v-input__control {
  min-height: auto;
}

.add-wrestler-dialog .v-text-field__details,
.add-wrestler-dialog .v-select__details {
  display: none;
}

.add-wrestler-dialog .v-messages {
  min-height: 0;
  display: none;
}

.add-wrestler-dialog .v-input__slot {
  padding: 8px 12px;
  min-height: 44px;
}
</style>
